import React from "react";
import { Alert, Box, Button, Link as MuiLink, Paper, Stack, Typography } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import SeoHelmet from "./seo/SeoHelmet";
import { absoluteUrl, buildBreadcrumbSchema, buildOrganizationSchema } from "./seo/schema";

const CONTACT_EMAIL = "contact@cyberpunk.pk";

const pageSx = {
  maxWidth: 900,
  mx: "auto",
  pb: 4,
};

const sectionSx = { p: { xs: 2, sm: 3 }, mb: 2 };

function TrustPage({ title, description, schema, children }) {
  return (
    <>
      <SeoHelmet
        title={`${title} | Cyberpunk`}
        description={description}
        canonical={absoluteUrl(`/${title.toLowerCase().replace(/\s+/g, "-")}`)}
        schema={[buildBreadcrumbSchema([{ name: "Home", path: "/" }, { name: title, path: `/${title.toLowerCase().replace(/\s+/g, "-")}` }]), ...(schema || [])]}
      />
      <Box sx={pageSx}>{children}</Box>
    </>
  );
}

export function AboutPage() {
  return (
    <TrustPage
      title="About Us"
      description="Learn about Cyberpunk's browser-based CDR analysis tools, original educational content, and focus on transparent telecom data workflows."
      schema={[buildOrganizationSchema()]}
    >
      <Paper elevation={0} sx={sectionSx}>
        <Typography variant="h5" component="h2" gutterBottom>About Cyberpunk</Typography>
        <Typography color="text.secondary" paragraph>
          Cyberpunk provides practical browser-based tools and educational guides for reviewing Call Detail Records (CDR), contact frequency, locations, device identifiers, and investigation workflows.
        </Typography>
        <Typography color="text.secondary" paragraph>
          The CDR Analyzer is designed for authorized analysts, researchers, and investigators who need a clear way to validate supported Excel or CSV exports, inspect patterns, and produce reproducible summaries.
        </Typography>
        <Typography color="text.secondary">
          Our articles are written to explain methodology, limitations, and evidence-handling considerations. Results are analytical leads, not proof of wrongdoing, and should be corroborated with lawful independent evidence.
        </Typography>
      </Paper>

      <Paper elevation={0} sx={sectionSx}>
        <Typography variant="h6" component="h2" gutterBottom>What We Publish</Typography>
        <Stack component="ul" spacing={1} sx={{ pl: 3, color: "text.secondary" }}>
          <li>Original CDR and telecom-analysis explanations.</li>
          <li>Practical guidance for validation, normalization, contacts, locations, IMEI, and timelines.</li>
          <li>Transparent notes about limitations, assumptions, and responsible interpretation.</li>
        </Stack>
      </Paper>
    </TrustPage>
  );
}

export function EditorialPolicyPage() {
  return (
    <TrustPage
      title="Editorial Policy"
      description="How Cyberpunk researches, reviews, updates, and corrects its CDR analysis guides and technical website content."
    >
      <Paper elevation={0} sx={sectionSx}>
        <Typography variant="h5" component="h2" gutterBottom>Editorial Policy</Typography>
        <Typography color="text.secondary" paragraph>Last updated: August 6, 2026</Typography>
        <Typography color="text.secondary">
          Cyberpunk publishes original, people-first guidance to help authorized users understand CDR file validation, analysis methods, limitations, and responsible reporting. Articles are published by the Cyberpunk Editorial Team and are intended to explain a practical workflow, not merely target a search phrase.
        </Typography>
      </Paper>

      <Paper elevation={0} sx={sectionSx}>
        <Typography variant="h6" component="h2" gutterBottom>Research and Review</Typography>
        <Stack component="ul" spacing={1} sx={{ pl: 3, color: "text.secondary" }}>
          <li>Guides are checked against the behavior and supported file formats of the Cyberpunk CDR Analyzer.</li>
          <li>Technical claims are reviewed for clear scope, reproducible steps, and appropriate limitations.</li>
          <li>Examples are illustrative and do not use confidential case files or identify private individuals.</li>
          <li>Legal and evidentiary references are general information only; readers must verify current law and their own authority.</li>
        </Stack>
      </Paper>

      <Paper elevation={0} sx={sectionSx}>
        <Typography variant="h6" component="h2" gutterBottom>Updates and Corrections</Typography>
        <Typography color="text.secondary" paragraph>
          An article displays its publication and latest-update dates. We revise content when the analyzer changes, a supported telecom layout changes, or a material error or ambiguity is identified. Substantive corrections are incorporated into the article and reflected in its updated date.
        </Typography>
        <Typography color="text.secondary">
          To report an error, unclear instruction, broken link, or attribution concern, email <MuiLink href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</MuiLink> with the article URL and the specific passage. Do not attach confidential CDR or case data.
        </Typography>
      </Paper>

      <Paper elevation={0} sx={sectionSx}>
        <Typography variant="h6" component="h2" gutterBottom>Independence and Advertising</Typography>
        <Typography color="text.secondary">
          Advertising does not determine article conclusions or the operation of the analyzer. Sponsored material, if published, will be clearly identified. Editorial content does not encourage ad clicks, and analytical observations must be independently verified before operational or legal use.
        </Typography>
      </Paper>
    </TrustPage>
  );
}

export function ContactPage() {
  return (
    <TrustPage
      title="Contact Us"
      description="Contact Cyberpunk for website, CDR Analyzer, advertising, privacy, or correction questions."
    >
      <Paper elevation={0} sx={sectionSx}>
        <Typography variant="h5" component="h2" gutterBottom>Contact Us</Typography>
        <Typography color="text.secondary" paragraph>
          For questions about the CDR Analyzer, website content, advertising, privacy, or a correction, email the site team. Please do not send confidential case files or personally identifiable information through an unsolicited email.
        </Typography>
        <Button component="a" href={`mailto:${CONTACT_EMAIL}`} variant="contained" sx={{ textTransform: "none" }}>
          Email {CONTACT_EMAIL}
        </Button>
      </Paper>

      <Alert severity="info" sx={{ mb: 2 }}>
        Use the analyzer only with data you are authorized to process. Uploaded CDR files are analyzed in the browser and should be handled according to your organization&apos;s retention and security rules.
      </Alert>

      <Paper elevation={0} sx={sectionSx}>
        <Typography variant="h6" component="h2" gutterBottom>Related Pages</Typography>
        <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
          <MuiLink component={RouterLink} to="/privacy-policy">Privacy Policy</MuiLink>
          <MuiLink component={RouterLink} to="/terms">Terms of Use</MuiLink>
          <MuiLink component={RouterLink} to="/cookie-policy">Cookie Policy</MuiLink>
        </Stack>
      </Paper>
    </TrustPage>
  );
}

export function PrivacyPolicyPage() {
  return (
    <TrustPage
      title="Privacy Policy"
      description="Cyberpunk Privacy Policy covering CDR file processing, Google Analytics, Google AdSense cookies, consent, and user rights."
    >
      <Paper elevation={0} sx={sectionSx}>
        <Typography variant="h5" component="h2" gutterBottom>Privacy Policy</Typography>
        <Typography color="text.secondary" paragraph>Last updated: July 24, 2026</Typography>
        <Typography color="text.secondary" paragraph>
          This policy explains how Cyberpunk handles information when you visit this website or use the CDR Analyzer. The service is intended for lawful, authorized analysis and is not intended for children under 13.
        </Typography>
      </Paper>

      <Paper elevation={0} sx={sectionSx}>
        <Typography variant="h6" component="h2" gutterBottom>Information We Process</Typography>
        <Stack component="ul" spacing={1} sx={{ pl: 3, color: "text.secondary" }}>
          <li><strong>Uploaded CDR files:</strong> the browser reads supported Excel or CSV files to produce analysis. Do not upload information you are not authorized to process.</li>
          <li><strong>Account and operational data:</strong> authenticated features may process login, case-management, audit, and application activity data needed to provide those features.</li>
          <li><strong>Analytics data:</strong> if you permit Google Analytics, Google may receive browser, device, and interaction information to measure and improve the site.</li>
          <li><strong>Advertising data:</strong> Google AdSense and other advertising partners may use cookies, web beacons, IP addresses, or similar identifiers to serve and measure ads.</li>
        </Stack>
      </Paper>

      <Paper elevation={0} sx={sectionSx}>
        <Typography variant="h6" component="h2" gutterBottom>Cookies, Ads, and Consent</Typography>
        <Typography color="text.secondary" paragraph>
          Google and third-party vendors may place and read cookies on your browser, including advertising cookies used to serve ads based on visits to this site or other sites. Where required, the site uses a Google-certified consent management platform for users in the EEA, UK, and Switzerland. You can change your choices through the consent message when it is shown.
        </Typography>
        <Typography color="text.secondary" paragraph>
          You can manage personalized advertising through <MuiLink href="https://www.google.com/settings/ads" target="_blank" rel="noreferrer">Google Ads Settings</MuiLink> or learn about third-party opt-out choices at <MuiLink href="https://optout.aboutads.info/" target="_blank" rel="noreferrer">aboutads.info</MuiLink>. You can also control cookies through your browser settings.
        </Typography>
        <Typography color="text.secondary">
          For more information about Google data use on partner sites, see <MuiLink href="https://policies.google.com/technologies/partner-sites" target="_blank" rel="noreferrer">How Google uses information from sites or apps that use its services</MuiLink>.
        </Typography>
      </Paper>

      <Paper elevation={0} sx={sectionSx}>
        <Typography variant="h6" component="h2" gutterBottom>Use, Sharing, and Retention</Typography>
        <Typography color="text.secondary" paragraph>
          We use information to operate, secure, improve, and communicate about the service. We do not sell uploaded CDR files. Information may be shared with service providers needed for hosting, analytics, advertising, security, or legal compliance, subject to their applicable terms and policies.
        </Typography>
        <Typography color="text.secondary">
          Retention depends on the feature and operational need. Browser-only CDR analysis remains subject to your browser and organization&apos;s storage controls; clear local browser data when appropriate and do not use the tool as a records archive.
        </Typography>
      </Paper>

      <Paper elevation={0} sx={sectionSx}>
        <Typography variant="h6" component="h2" gutterBottom>Questions and Requests</Typography>
        <Typography color="text.secondary">
          For privacy questions or correction requests, contact <MuiLink href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</MuiLink>. We may need enough information to verify and respond to a request without asking you to send sensitive CDR data.
        </Typography>
      </Paper>
    </TrustPage>
  );
}

export function CookiePolicyPage() {
  return (
    <TrustPage
      title="Cookie Policy"
      description="Cyberpunk Cookie Policy explaining analytics, advertising, consent, and browser cookie controls."
    >
      <Paper elevation={0} sx={sectionSx}>
        <Typography variant="h5" component="h2" gutterBottom>Cookie Policy</Typography>
        <Typography color="text.secondary" paragraph>
          Cyberpunk uses local storage for application preferences and consent choices. Google Analytics may use measurement technologies when analytics consent is granted. Google AdSense and advertising partners may use cookies or similar technologies for ad delivery, frequency management, and measurement.
        </Typography>
        <Typography color="text.secondary" paragraph>
          You can reject or change optional consent choices when the consent message is displayed and can manage cookies through your browser settings. Advertising opt-out information is available through <MuiLink href="https://www.google.com/settings/ads" target="_blank" rel="noreferrer">Google Ads Settings</MuiLink> and <MuiLink href="https://optout.aboutads.info/" target="_blank" rel="noreferrer">aboutads.info</MuiLink>.
        </Typography>
        <Typography color="text.secondary">See the <MuiLink component={RouterLink} to="/privacy-policy">Privacy Policy</MuiLink> for the full disclosure of data use and third-party services.</Typography>
      </Paper>
    </TrustPage>
  );
}

export function TermsPage() {
  return (
    <TrustPage
      title="Terms"
      description="Cyberpunk Terms of Use for the CDR Analyzer, educational content, authorized data processing, and responsible use."
    >
      <Paper elevation={0} sx={sectionSx}>
        <Typography variant="h5" component="h2" gutterBottom>Terms of Use</Typography>
        <Typography color="text.secondary" paragraph>Last updated: July 24, 2026</Typography>
        <Typography color="text.secondary" paragraph>
          By using Cyberpunk, you agree to use the website and tools lawfully, respectfully, and only with data you are authorized to access and analyze.
        </Typography>
      </Paper>
      <Paper elevation={0} sx={sectionSx}>
        <Typography variant="h6" component="h2" gutterBottom>Responsible Use</Typography>
        <Stack component="ul" spacing={1} sx={{ pl: 3, color: "text.secondary" }}>
          <li>Do not upload stolen, unlawfully obtained, or unauthorized personal data.</li>
          <li>Do not use results to harass, threaten, discriminate against, or unlawfully surveil another person.</li>
          <li>Validate analytical results against the original records and independent evidence before relying on them.</li>
          <li>Do not click your own ads, ask others to click ads, or interact with ads deceptively.</li>
        </Stack>
      </Paper>
      <Paper elevation={0} sx={sectionSx}>
        <Typography variant="h6" component="h2" gutterBottom>Limitations</Typography>
        <Typography color="text.secondary" paragraph>
          The website provides analytical tools and educational information. It does not provide legal advice, investigative authorization, telecom records, or a guarantee that every result is complete or suitable for a particular case.
        </Typography>
        <Typography color="text.secondary">Questions about these terms can be sent to <MuiLink href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</MuiLink>.</Typography>
      </Paper>
    </TrustPage>
  );
}
